import { useMemo } from 'react';
import { useIncident, usePeriods } from 'components/IAP/hooks/incidentHooks';
import { useIncidentInfo } from 'components/IAP/hooks/incidentInfoHooks';
import { useOptionalICSContext } from 'components/IAP/planningView/ICSContext';
import {
  resolveOperationalPeriodWindow,
  type OperationalPeriodWindow,
} from './operationalPeriodWindow';

/**
 * The operational period window for the package currently on screen, matching
 * what the export will print.
 *
 * Pass a period number to pin the window, which is what the ICS 204 does for
 * the period it targets. Otherwise the view being browsed decides: the working
 * package shows the scheduled next-period window, a historical one shows its
 * own recorded window. Only that second form needs the planning view's context,
 * so a pinned caller is free to render anywhere, including a dialog outside the
 * provider.
 */
export function useOperationalPeriodWindow(
  periodNumber?: number
): OperationalPeriodWindow {
  const incident = useIncident();
  const periods = usePeriods();
  const { data: incidentInfo } = useIncidentInfo();
  const icsContext = useOptionalICSContext();

  const targetPeriod = periodNumber ?? icsContext?.effectivePeriodNumber ?? 0;
  // A pinned period is always a real one, so it never takes the working branch.
  const targetIsWorking = periodNumber == null && !!icsContext?.isWorking;

  const nextPeriodStart = incidentInfo?.nextOperationalPeriodStartDateTime;
  const nextPeriodEnd = incidentInfo?.nextOperationalPeriodEndDateTime;
  const incidentCreated = incident.data?.incident_created;

  return useMemo(
    () =>
      resolveOperationalPeriodWindow({
        periodNumber: targetPeriod,
        isWorking: targetIsWorking,
        periods: periods.data,
        nextPeriodStart,
        nextPeriodEnd,
        incidentCreated,
      }),
    [
      incidentCreated,
      nextPeriodEnd,
      nextPeriodStart,
      periods.data,
      targetIsWorking,
      targetPeriod,
    ]
  );
}
